import type { FunctionComponent } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { useLiveArticleScrollContext } from "scmp-app/components/article/article-render/live-article/contexts";
import type { timelineLiveArticleLiveContent$key } from "scmp-app/queries/__generated__/timelineLiveArticleLiveContent.graphql";

import { Container, StyledLiveArticleTimelineItem } from "./styles";

type Props = {
  className?: string;
  deepLinkPosition?: number;
  reference: timelineLiveArticleLiveContent$key;
};

export const LiveArticleTimeline: FunctionComponent<Props> = ({
  className,
  deepLinkPosition = 0,
  reference,
}) => {
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const { setTimelineScrollContainerReference } = useLiveArticleScrollContext();

  const contents = useFragment(
    graphql`
      fragment timelineLiveArticleLiveContent on LiveContent @relay(plural: true) {
        customTime
        entityUuid
        ...itemLiveArticleTimelineLiveContent
      }
    `,
    reference,
  );

  const liveContents = useMemo(
    () =>
      contents?.filter(notEmpty)?.toSorted((a, b) => (b?.customTime ?? 0) - (a?.customTime ?? 0)) ??
      [],
    [contents],
  );

  const renderLiveContents = useMemo(
    () => liveContents.slice(deepLinkPosition),
    [deepLinkPosition, liveContents],
  );

  const capture = useCallback(
    (element: HTMLDivElement) => {
      if (!element || !setTimelineScrollContainerReference) return;
      setTimelineScrollContainerReference(element);
    },
    [setTimelineScrollContainerReference],
  );

  return (
    <Container $stickyStackedHeight={appBarStickyHeight} className={className} ref={capture}>
      {renderLiveContents.map((liveContent, index) => (
        <StyledLiveArticleTimelineItem
          key={liveContent.entityUuid ?? index}
          reference={liveContent}
        />
      ))}
    </Container>
  );
};

LiveArticleTimeline.displayName = "LiveArticleTimeline";
